import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const REFRESH_INTERVAL_MS = 3600000; // 1 hour
const LS_KEY = 'dashboard_auto_refresh_enabled';
const CACHE_KEY_PREFIX = 'dashboard_data_cache_v2:';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

// 2026-07-15: Cache key now includes user_id to prevent cross-user pollution.
// Old v1 keys are ignored (naturally expire via unused).
const _cacheKey = (userId, branch, router) => `${CACHE_KEY_PREFIX}${userId || 'anon'}::${branch || 'all'}::${router || 'all'}`;

// Read cache; return payload if fresh (< TTL), else null.
const _readCache = (userId, branch, router) => {
  try {
    const raw = localStorage.getItem(_cacheKey(userId, branch, router));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ts || (Date.now() - parsed.ts) > CACHE_TTL_MS) return null;
    return parsed.data;
  } catch { return null; }
};

const _writeCache = (userId, branch, router, data) => {
  try {
    localStorage.setItem(_cacheKey(userId, branch, router), JSON.stringify({ ts: Date.now(), data }));
  } catch { /* quota may be full — ignore */ }
};

/**
 * Centralized data-fetching hook for all Dashboard variants
 * (DashboardV2 / DashboardLight / DashboardDark).
 *
 * Features:
 *  - Parallel fetch of 7 dashboard endpoints (all tolerant to failure)
 *  - Auto re-fetch every 60s (toggleable, preference persisted in localStorage)
 *  - Manual "refresh now" trigger
 *  - Re-fetches on selectedBranch change
 *
 * Returns:
 *  { stats, routers, packageStats, registrations, revenueTrend, recentCustomers,
 *    loading, selectedBranch,
 *    isAutoRefresh, toggleAutoRefresh, refreshNow, lastRefreshedAt }
 */
const useDashboardData = () => {
  const { selectedBranch, selectedRouter } = useApp();
  const { user } = useAuth();
  const userId = user?.id || user?.uuid || 'anon';

  // 2026-07-15: Cache-first hydration — instant paint when user opens dashboard.
  // Reads localStorage cache for current userId+branch+router combo; if fresh (<5min),
  // populate initial state and skip loading spinner. Background refresh still fires.
  const _initialCached = _readCache(userId, selectedBranch, selectedRouter);

  const [stats, setStats] = useState(_initialCached?.stats || null);
  const [routers, setRouters] = useState(_initialCached?.routers || []);
  const [packageStats, setPackageStats] = useState(_initialCached?.packageStats || []);
  const [registrations, setRegistrations] = useState(_initialCached?.registrations || []);
  const [revenueTrend, setRevenueTrend] = useState(_initialCached?.revenueTrend || []);
  const [recentCustomers, setRecentCustomers] = useState(_initialCached?.recentCustomers || []);
  const [serviceInfo, setServiceInfo] = useState(_initialCached?.serviceInfo || null);
  const [loading, setLoading] = useState(!_initialCached);  // instant when we have cache
  const [lastRefreshedAt, setLastRefreshedAt] = useState(_initialCached ? new Date() : null);

  const [isAutoRefresh, setIsAutoRefresh] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_KEY);
      return saved === null ? true : saved === 'true';
    } catch { return true; }
  });

  const intervalRef = useRef(null);
  const branchRef = useRef(selectedBranch);
  branchRef.current = selectedBranch;
  const routerRef = useRef(selectedRouter);
  routerRef.current = selectedRouter;

  // Silent fetch: does not flip `loading` (to avoid flicker during auto-refresh).
  const fetchAll = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    const branch = branchRef.current;
    const router = routerRef.current;
    // Build query helpers that support BOTH branch and router filters.
    const parts = [];
    if (branch && branch !== 'all') parts.push(`branch_id=${branch}`);
    if (router && router !== 'all') parts.push(`router_id=${router}`);
    const branchQ = parts.length ? '?' + parts.join('&') : '';
    const branchQS = parts.length ? '&' + parts.join('&') : '';

    const calls = await Promise.allSettled([
      axios.get(`${API_URL}/api/v2/dashboard/stats${branchQ}`, { headers }),
      axios.get(`${API_URL}/api/v2/customers?limit=8${branchQS}`, { headers }),
      axios.get(`${API_URL}/api/dashboard/package-stats${branchQ}`, { headers }),
      axios.get(`${API_URL}/api/dashboard/registrations-summary?days=30${branchQS}`, { headers }),
      axios.get(`${API_URL}/api/v2/dashboard/service-info`, { headers }).catch(() => null),
      axios.get(`${API_URL}/api/v2/mpanel/available-routers`, { headers }).catch(() => null),
      axios.get(`${API_URL}/api/v2/revenue/chart-data?period=yearly&year=${new Date().getFullYear()}${branchQS}`, { headers }).catch(() => null),
      axios.get(`${API_URL}/api/v2/mikrotiks/active-sessions-count`, { headers }).catch(() => null),
    ]);
    if (calls[0].status === 'fulfilled') setStats(calls[0].value.data?.data || {});
    if (calls[1].status === 'fulfilled') setRecentCustomers(calls[1].value.data?.data || []);
    if (calls[2].status === 'fulfilled' && calls[2].value.data?.success) {
      const raw = calls[2].value.data.data || {};
      const branches = raw.branches || [];
      const allBranch = branches.find(b => b.branch_code === 'ALL' || b.branch_id === 0) || branches[0];
      if (allBranch && Array.isArray(allBranch.packages)) {
        setPackageStats(allBranch.packages.map(p => ({ name: p.package_name, count: p.count })).slice(0, 8));
      }
    }
    if (calls[3].status === 'fulfilled' && calls[3].value.data?.success) {
      const summary = calls[3].value.data.data || {};
      const daily = (summary.daily || []).map(d => ({ date: d.date || d.day_name, count: d.count || 0 }));
      setRegistrations(daily);
    }
    if (calls[4] && calls[4].status === 'fulfilled' && calls[4].value?.data?.success) setServiceInfo(calls[4].value.data.data || null);
    if (calls[5] && calls[5].status === 'fulfilled') {
      const baseRouters = calls[5].value?.data?.data || calls[5].value?.data || [];
      // Merge live active-session counts from /api/v2/mikrotiks/active-sessions-count
      const acData = (calls[7] && calls[7].status === 'fulfilled') ? (calls[7].value?.data?.data || {}) : {};
      const merged = baseRouters.map(r => {
        const ac = acData[String(r.id)];
        if (ac) {
          return { ...r, active_sessions: ac.count, active_status: ac.status, active_error: ac.error };
        }
        return r;
      });
      setRouters(merged);
    }
    if (calls[6] && calls[6].status === 'fulfilled') setRevenueTrend(calls[6].value?.data?.data?.trend_data || []);
    setLastRefreshedAt(new Date());
    if (!silent) setLoading(false);

    // 2026-07-15: Snapshot current fetch results into cache for next mount's instant paint.
    try {
      const snapshot = {
        stats: (calls[0].status === 'fulfilled') ? (calls[0].value.data?.data || {}) : null,
        recentCustomers: (calls[1].status === 'fulfilled') ? (calls[1].value.data?.data || []) : [],
        packageStats: (() => {
          if (calls[2].status === 'fulfilled' && calls[2].value.data?.success) {
            const raw = calls[2].value.data.data || {};
            const branches = raw.branches || [];
            const allBranch = branches.find(b => b.branch_code === 'ALL' || b.branch_id === 0) || branches[0];
            if (allBranch && Array.isArray(allBranch.packages)) {
              return allBranch.packages.map(p => ({ name: p.package_name, count: p.count })).slice(0, 8);
            }
          }
          return [];
        })(),
        registrations: (() => {
          if (calls[3].status === 'fulfilled' && calls[3].value.data?.success) {
            const summary = calls[3].value.data.data || {};
            return (summary.daily || []).map(d => ({ date: d.date || d.day_name, count: d.count || 0 }));
          }
          return [];
        })(),
        serviceInfo: (calls[4] && calls[4].status === 'fulfilled' && calls[4].value?.data?.success) ? (calls[4].value.data.data || null) : null,
        routers: (() => {
          if (calls[5] && calls[5].status === 'fulfilled') {
            const base = calls[5].value?.data?.data || calls[5].value?.data || [];
            const acData = (calls[7] && calls[7].status === 'fulfilled') ? (calls[7].value?.data?.data || {}) : {};
            return base.map(r => {
              const ac = acData[String(r.id)];
              if (ac) return { ...r, active_sessions: ac.count, active_status: ac.status, active_error: ac.error };
              return r;
            });
          }
          return [];
        })(),
        revenueTrend: (calls[6] && calls[6].status === 'fulfilled') ? (calls[6].value?.data?.data?.trend_data || []) : [],
      };
      _writeCache(branchRef.current, routerRef.current, snapshot);
    } catch (_e) { /* ignore cache write errors */ }
  }, []);

  // Initial + branch/router-change fetch. If we have fresh cache for this combo,
  // rehydrate instantly and fetch silently in background.
  useEffect(() => {
    const cached = _readCache(selectedBranch, selectedRouter);
    if (cached) {
      // Instant paint
      setStats(cached.stats || null);
      setRouters(cached.routers || []);
      setPackageStats(cached.packageStats || []);
      setRegistrations(cached.registrations || []);
      setRevenueTrend(cached.revenueTrend || []);
      setRecentCustomers(cached.recentCustomers || []);
      setServiceInfo(cached.serviceInfo || null);
      setLoading(false);
      setLastRefreshedAt(new Date());
      // Silent background refresh to keep data fresh
      fetchAll({ silent: true });
    } else {
      fetchAll({ silent: false });
    }
  }, [selectedBranch, selectedRouter, fetchAll]);

  // Auto-refresh interval (silent).
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (isAutoRefresh) {
      intervalRef.current = setInterval(() => {
        fetchAll({ silent: true });
      }, REFRESH_INTERVAL_MS);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isAutoRefresh, fetchAll]);

  const toggleAutoRefresh = useCallback(() => {
    setIsAutoRefresh((prev) => {
      const next = !prev;
      try { localStorage.setItem(LS_KEY, String(next)); } catch {}
      return next;
    });
  }, []);

  const refreshNow = useCallback(() => {
    fetchAll({ silent: false });
  }, [fetchAll]);

  return {
    stats, routers, packageStats, registrations, revenueTrend, recentCustomers, serviceInfo,
    loading, selectedBranch, selectedRouter,
    isAutoRefresh, toggleAutoRefresh, refreshNow, lastRefreshedAt,
    refreshIntervalMs: REFRESH_INTERVAL_MS,
  };
};

export default useDashboardData;
